// Email Deliverability — platform operators see how much mail is landing, failing,
// or being suppressed (e.g. by marketing consent) across all tenants. Real data from
// notification-service's delivery log; recipients are masked.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Card, Table, Tag, Row, Col, Statistic, Typography, Space, Button, Select, message, Progress, Tooltip,
} from 'antd';
import { MailOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { getDeliverability, type Deliverability } from '../../services/deliverabilityService';

const { Title, Text } = Typography;

const fmtTime = (s: string) => (s ? fmtDateTime(s) : '—');

const statusColor: Record<string, string> = {
  sent: 'green', delivered: 'green',
  failed: 'red', error: 'red', bounced: 'red',
  suppressed: 'orange', skipped: 'orange',
};

export const DeliverabilityPage: React.FC = () => {
  const [data, setData] = useState<Deliverability | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = (d = days) => {
    setLoading(true);
    getDeliverability(d)
      .then(setData)
      .catch((e) => message.error(e?.response?.data?.message || 'Could not load deliverability.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = data?.totals;

  return (
    <div className="p-1">
      <div className="flex items-center justify-between mb-4">
        <Space align="center">
          <MailOutlined style={{ fontSize: 22 }} />
          <Title level={3} style={{ margin: 0 }}>Email Deliverability</Title>
        </Space>
        <Space>
          <Select
            value={days}
            style={{ width: 130 }}
            onChange={(d) => { setDays(d); load(d); }}
            options={[
              { value: 7, label: 'Last 7 days' },
              { value: 30, label: 'Last 30 days' },
              { value: 90, label: 'Last 90 days' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Refresh</Button>
        </Space>
      </div>

      <Text type="secondary">
        Cross-tenant delivery of all transactional + Dana mail. "Suppressed" includes
        messages held back by a recipient's consent choice (e.g. marketing opt-out).
      </Text>

      <Row gutter={16} className="mt-4 mb-4">
        <Col span={5}><Card><Statistic title="Sent" value={t?.sent ?? 0} valueStyle={{ color: 'var(--color-success)' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="Failed" value={t?.failed ?? 0} valueStyle={{ color: 'var(--color-danger)' }} /></Card></Col>
        <Col span={5}><Card><Statistic title="Suppressed" value={t?.suppressed ?? 0} valueStyle={{ color: '#ea580c' }} /></Card></Col>
        <Col span={4}><Card><Statistic title="Total" value={t?.total ?? 0} /></Card></Col>
        <Col span={5}>
          <Card>
            <Tooltip title="Sent / (Sent + Failed)">
              <div className="text-xs text-muted-foreground mb-1">Delivery rate</div>
            </Tooltip>
            <Progress
              percent={data?.delivery_rate ?? 0}
              size="small"
              status={(data?.delivery_rate ?? 100) >= 95 ? 'success' : 'exception'}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="By channel" size="small" className="mb-4">
            <Table
              rowKey="channel"
              size="small"
              loading={loading}
              pagination={false}
              dataSource={data?.by_channel ?? []}
              columns={[
                { title: 'Channel', dataIndex: 'channel' },
                { title: 'Sent', dataIndex: 'sent' },
                { title: 'Failed', dataIndex: 'failed' },
                { title: 'Suppressed', dataIndex: 'suppressed' },
              ]}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="By message type" size="small" className="mb-4">
            <Table
              rowKey="type"
              size="small"
              loading={loading}
              pagination={false}
              dataSource={data?.by_type ?? []}
              columns={[
                { title: 'Type', dataIndex: 'type' },
                { title: 'Total', dataIndex: 'total' },
                { title: 'Failed', dataIndex: 'failed', render: (v: number) => v ? <Tag color="red">{v}</Tag> : 0 },
                { title: 'Suppressed', dataIndex: 'suppressed', render: (v: number) => v ? <Tag color="orange">{v}</Tag> : 0 },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={<Space><WarningOutlined /> Recent failures &amp; suppressions</Space>}
        size="small"
      >
        <Table
          rowKey={(r) => `${r.recipient}-${r.created_at}`}
          size="small"
          loading={loading}
          dataSource={data?.recent_issues ?? []}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'When', dataIndex: 'created_at', render: fmtTime, width: 180 },
            { title: 'Recipient', dataIndex: 'recipient' },
            { title: 'Type', dataIndex: 'type' },
            { title: 'Channel', dataIndex: 'channel' },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (s: string) => <Tag color={statusColor[s.toLowerCase()] || 'default'}>{s}</Tag>,
            },
            { title: 'Reason', dataIndex: 'reason', ellipsis: true },
          ]}
        />
      </Card>
    </div>
  );
};

export default DeliverabilityPage;
